// @ts-ignore
import { Component, EventEmitter, HostListener, OnInit, OnDestroy, Output } from '@angular/core';
// @ts-ignore
import { CommonModule } from '@angular/common';
// @ts-ignore
import { FormsModule } from '@angular/forms';
import * as XLSX from 'xlsx';
// @ts-ignore
import { firstValueFrom, Subscription, timeout } from 'rxjs';
import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { ProdutoService } from '../../services/produto.service';
import { ProdutoBusca } from '../../models/estoque.model';
import { EmpresaStateService } from '../../services/empresa-state.service';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';

/**
 * Tela "Lista de Produtos" (pedido do usuário, 2026-09-18): uma tabela
 * simples com TODOS os produtos do Sankhya, no mesmo padrão visual da lista
 * de Pedidos de Venda (page-header + card + tabela + paginação), mas de
 * propósito bem mais simples -- só Código/PN/Descrição/Status e um botão
 * Editar por linha -- pra não ficar difícil de entender pro usuário final.
 *
 * Importante: essa tela NÃO altera em nada a tela de Cadastrar Produto
 * (duplicar-produto) -- ela só abre a partir daqui, via o mesmo fluxo que já
 * existia (botão "Novo Produto" -> app-duplicar-produto). A busca de dados
 * reaproveita o padrão já usado em "Produtos Modelo" (buscarProdutosModelo),
 * só que num endpoint novo (listarProdutos) que também devolve TODOS os
 * produtos quando não há termo digitado.
 */
@Component({
  selector: 'app-produtos-lista',
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingIndicatorComponent],
  templateUrl: './produtos-lista.component.html',
  styleUrl: './produtos-lista.component.css'
})
export class ProdutosListaComponent implements OnInit, OnDestroy {
  @Output() novoProduto = new EventEmitter<string | void>();
  @Output() editarProduto = new EventEmitter<string>();
  @Output() clonarProduto = new EventEmitter<{ codprod: string; partNumber: string; nome: string; imagem?: string }>();

  carregando = false;
  erro = '';
  produtos: ProdutoBusca[] = [];

  termoBusca = '';

  /** Inicia com os 10 mais recentes; "Atualizar" passa a listar todos os produtos. */
  listaCompleta = false;

  onAtualizar(): void {
    this.listaCompleta = true;
    this.cachePaginas.clear();
    this.cacheEstoque.clear();
    void this.carregar();
  }

  // ── Paginação (client-side, mesmo padrão da lista de Pedidos de Venda) ──
  paginaAtual = 1;
  itensPorPagina = 10;
  readonly opcoesPorPagina = [10, 50, 100];

  empresaId = '';
  menuAberto: any = null;
  /** Posicao (fixed) do menu de acoes da linha: calculada a partir do botao clicado. */
  menuPos: { top: number; right: number } | null = null;

  toggleMenuAcoes(p: any, event: Event): void {
    event.stopPropagation();
    if (this.menuAberto === p) { this.menuAberto = null; return; }
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const alturaMenu = 90;
    const abreParaCima = rect.bottom + 6 + alturaMenu > window.innerHeight;
    this.menuPos = {
      top: abreParaCima ? Math.max(8, rect.top - 6 - alturaMenu) : rect.bottom + 6,
      right: Math.max(8, window.innerWidth - rect.right)
    };
    this.menuAberto = p;
  }

  /** Rolar a pagina ou redimensionar a janela fecha o menu (ele e fixed e ficaria descolado do botao). */
  @HostListener('window:resize')
  @HostListener('window:scroll')
  fecharMenuAcoesAoRolar(): void {
    if (this.menuAberto) this.menuAberto = null;
  }
  funcoesMenuAberto = false;

  // ── Modal: estoque do item nas demais filiais ──
  modalFiliaisAberto = false;
  modalFiliaisCarregando = false;
  modalFiliaisProduto: ProdutoBusca | null = null;
  modalFiliaisItens: { empresaId: number; empresa: string; qtde: number; local: string; atual: boolean }[] = [];
  modalFiliaisErro = '';
  modalFiliaisSemSaldoOutras = false;

  async abrirEstoqueFiliais(p: ProdutoBusca, ev?: Event): Promise<void> {
    ev?.stopPropagation();
    this.modalFiliaisProduto = p;
    this.modalFiliaisItens = [];
    this.modalFiliaisErro = '';
    this.modalFiliaisAberto = true;
    this.modalFiliaisCarregando = true;
    try {
      const r = await firstValueFrom(this.produtoService.estoqueFiliaisAerosys(
        this.codProdStr(p), Number(this.empresaStateService.selecionada) || 1));
      const todas = r?.itens || [];
      // Filial atual sempre aparece; as demais so se tiverem saldo.
      this.modalFiliaisItens = todas.filter(i => i.atual || (i.qtde || 0) !== 0);
      this.modalFiliaisSemSaldoOutras = todas.length > 0 && !todas.some(i => !i.atual && (i.qtde || 0) !== 0);
      if (!todas.length) this.modalFiliaisErro = 'Nenhuma filial encontrada para este item no Aerosys.';
    } catch {
      this.modalFiliaisErro = 'Não foi possível carregar o estoque das filiais.';
    } finally {
      this.modalFiliaisCarregando = false;
    }
  }

  fecharEstoqueFiliais(): void {
    this.modalFiliaisAberto = false;
  }

  get totalEstoqueFiliais(): number {
    return this.modalFiliaisItens.reduce((s, i) => s + (i.qtde || 0), 0);
  }

  // ── Modal: ultimas 3 cotacoes (clique no Preco Fab.) ──
  modalCotAberto = false;
  modalCotCarregando = false;
  modalCotErro = '';
  modalCotProduto: ProdutoBusca | null = null;
  modalCotItens: {
    data: string; preco: number; custoPct: number; totalCusto: number; lucroPct: number;
    venda: number; vendaReais: number | null; variacao: number; core: number;
  }[] = [];

  /** Fabricante nacional: valores em R$ (sem conversao pelo dolar). */
  get cotNacional(): boolean {
    return !!(this.modalCotProduto as any)?.origemNacional;
  }

  private num(v: any): number {
    const n = parseFloat(String(v ?? '').replace(',', '.'));
    return isNaN(n) ? 0 : n;
  }
  private r2(v: number): number { return Math.round((v + Number.EPSILON) * 100) / 100; }

  async abrirCotacoes(p: ProdutoBusca, ev?: Event): Promise<void> {
    ev?.stopPropagation();
    this.modalCotProduto = p;
    this.modalCotItens = [];
    this.modalCotErro = '';
    this.modalCotAberto = true;
    this.modalCotCarregando = true;
    try {
      const cod = Number(p.codprod ?? p.codigo);
      const [rc, rp]: any[] = await Promise.all([
        firstValueFrom(this.sankhyaGateway.cotacoesProdutoEdicao({ CODPROD: cod })),
        firstValueFrom(this.sankhyaGateway.produtoSelecionadoEdicao({ CODPROD: cod }))
      ]);
      const lista: any[] = (rc?.responseBody ?? rc)?.lista || [];
      const prod: any = (rp?.responseBody ?? rp)?.produto || {};
      const custo = this.num(prod.margCusto);
      const lucro = this.num(prod.margLucro);
      const nacional = !!(p as any).origemNacional;
      // Cotacoes consecutivas iguais (mesmo Preco Fab. e Core) aparecem uma vez so: a atualizacao de margens
      // em lote registra uma cotacao com o mesmo preco, o que parecia linha duplicada.
      const distintas = lista.filter((c, i) => i === 0
        || this.num(c.preco) !== this.num(lista[i - 1].preco) || this.num(c.core) !== this.num(lista[i - 1].core));
      // Mostra SÓ a cotação mais recente (a mesma da tela do produto cadastrado) -- antes
      // mostrava as 3 últimas; para voltar, trocar slice(0, 1) por slice(0, 3).
      this.modalCotItens = distintas.slice(0, 1).map((c) => {
        const preco = this.num(c.preco);
        const totalCusto = this.r2(preco * (1 + custo / 100));
        const venda = this.r2(totalCusto * (1 + lucro / 100));
        const vendaReais = nacional ? venda : (this.cotacaoDolar > 0 ? this.r2(venda * this.cotacaoDolar) : null);
        return { data: c.data || '', preco, custoPct: custo, totalCusto, lucroPct: lucro, venda, vendaReais, variacao: this.num(c.variacao), core: this.num(c.core) };
      });
      if (!this.modalCotItens.length) this.modalCotErro = 'Nenhuma cotação registrada para este produto.';
    } catch {
      this.modalCotErro = 'Não foi possível carregar as cotações.';
    } finally {
      this.modalCotCarregando = false;
    }
  }

  fecharCotacoes(): void {
    this.modalCotAberto = false;
  }

  // ── Atualizar Margem de Custo/Lucro (em lote) ──
  readonly opcoesMargem = [
    { id: 'CL_PF', label: 'Custo e Lucro - Produtos e Fabricante', custo: true, lucro: true, prod: true, marca: true },
    { id: 'C_PF', label: 'Só Custo - Produtos e Fabricante', custo: true, lucro: false, prod: true, marca: true },
    { id: 'L_PF', label: 'Só Lucro - Produtos e Fabricante', custo: false, lucro: true, prod: true, marca: true },
    { id: 'L_P', label: 'Só Lucro - Produtos', custo: false, lucro: true, prod: true, marca: false },
    { id: 'C_P', label: 'Só Custo - Produtos', custo: true, lucro: false, prod: true, marca: false },
    { id: 'F', label: 'Só Fabricante (Custo e Lucro)', custo: true, lucro: true, prod: false, marca: true },
    { id: 'F_C', label: 'Só Fabricante (Custo)', custo: true, lucro: false, prod: false, marca: true },
    { id: 'F_L', label: 'Só Fabricante (Lucro)', custo: false, lucro: true, prod: false, marca: true }
  ];
  modalMargemAberto = false;
  margemEtapaConfirmar = false;
  margemAlterar = 'CL_PF';
  margemAtualizar: 'FABRICANTE' | 'TODOS' = 'FABRICANTE';
  margemCusto = '';
  margemLucro = '';
  margemCodMarca = '';
  margemNomeMarca = '';
  margemErro = '';
  processandoMargem = false;
  margemPct = 0;
  margemMsg = '';
  private marcasMargem: { valor: string; label: string }[] = [];
  modalMarcaMargemAberto = false;
  filtroMarcaMargem = '';

  get opcaoMargem() { return this.opcoesMargem.find(o => o.id === this.margemAlterar)!; }

  get marcasMargemFiltradas(): { valor: string; label: string }[] {
    const f = this.filtroMarcaMargem.trim().toUpperCase();
    return f ? this.marcasMargem.filter(m => m.label.toUpperCase().includes(f)) : this.marcasMargem;
  }

  abrirModalMargem(): void {
    this.funcoesMenuAberto = false;
    this.margemAlterar = 'CL_PF';
    this.margemAtualizar = 'FABRICANTE';
    this.margemCusto = '';
    this.margemLucro = '';
    this.margemCodMarca = '';
    this.margemNomeMarca = '';
    this.margemErro = '';
    this.margemEtapaConfirmar = false;
    this.modalMargemAberto = true;
    void this.garantirMarcasMargem();
  }

  fecharModalMargem(): void {
    if (this.processandoMargem) return;
    this.modalMargemAberto = false;
    this.modalMarcaMargemAberto = false;
  }

  private async garantirMarcasMargem(): Promise<void> {
    if (this.marcasMargem.length) return;
    try {
      this.marcasMargem = (await firstValueFrom(this.produtoService.buscarMarcas())).map(m => ({ valor: String(m.valor), label: m.label }));
    } catch { this.marcasMargem = []; }
  }

  onMargemAtualizarChange(): void {
    if (this.margemAtualizar === 'TODOS') { this.margemCodMarca = ''; this.margemNomeMarca = ''; }
  }

  onMargemCodMarcaChange(): void {
    const cod = this.margemCodMarca.trim();
    const m = this.marcasMargem.find(x => x.valor === cod);
    this.margemNomeMarca = m ? (m.label.includes(' - ') ? m.label.substring(m.label.indexOf(' - ') + 3) : m.label) : '';
  }

  abrirModalMarcaMargem(): void {
    if (this.margemAtualizar !== 'FABRICANTE') return;
    this.filtroMarcaMargem = '';
    this.modalMarcaMargemAberto = true;
    void this.garantirMarcasMargem();
    setTimeout(() => (document.querySelector('.mg-marca-input') as HTMLInputElement | null)?.focus(), 50);
  }

  selecionarMarcaMargem(m: { valor: string; label: string }): void {
    this.margemCodMarca = m.valor;
    this.onMargemCodMarcaChange();
    this.modalMarcaMargemAberto = false;
  }

  private numeroMargem(txt: string): number | null {
    const s = String(txt ?? '').trim();
    if (!s) return null;
    const v = s.includes(',') ? parseFloat(s.replace(/\./g, '').replace(',', '.')) : parseFloat(s);
    return isNaN(v) || v < 0 ? null : v;
  }

  /** Rotulo dos campos conforme a opcao marcada (ex.: "Custo (%) - Produtos e Fabricante"). */
  get rotuloDestinoMargem(): string {
    const o = this.opcaoMargem;
    return o.prod && o.marca ? 'Produtos e Fabricante' : o.prod ? 'Produtos' : 'Fabricante';
  }
  get rotuloCustoMargem(): string { return `Custo (%) - ${this.rotuloDestinoMargem}`; }
  get rotuloLucroMargem(): string { return `Lucro (%) - ${this.rotuloDestinoMargem}`; }

  margemOk(): void {
    this.margemErro = '';
    if (this.margemAtualizar === 'FABRICANTE' && (!this.margemCodMarca.trim() || !this.margemNomeMarca)) {
      this.margemErro = 'Selecione o fabricante.';
      return;
    }
    const o = this.opcaoMargem;
    if (o.custo && this.numeroMargem(this.margemCusto) == null) {
      this.margemErro = 'Informe um Custo (%) válido.';
      return;
    }
    if (o.lucro && this.numeroMargem(this.margemLucro) == null) {
      this.margemErro = 'Informe um Lucro (%) válido.';
      return;
    }
    this.margemEtapaConfirmar = true;
  }

  get resumoMargem(): string {
    const o = this.opcaoMargem;
    const alvo = this.margemAtualizar === 'TODOS' ? 'TODOS os produtos/fabricantes' : `o fabricante ${this.margemCodMarca} - ${this.margemNomeMarca}`;
    const partes: string[] = [];
    if (o.custo) partes.push(`Custo = ${this.margemCusto}%`);
    if (o.lucro) partes.push(`Lucro = ${this.margemLucro}%`);
    return `${o.label}: definir ${partes.join(' e ')} em ${alvo}.`;
  }

  async confirmarMargem(): Promise<void> {
    const o = this.opcaoMargem;
    const custo = o.custo ? this.numeroMargem(this.margemCusto) : null;
    const lucro = o.lucro ? this.numeroMargem(this.margemLucro) : null;
    if ((o.custo && custo == null) || (o.lucro && lucro == null)) return;
    const corpo: Record<string, unknown> = {
      CODMARCA: this.margemAtualizar === 'FABRICANTE' ? this.margemCodMarca.trim() : '',
      ALTERAR_PRODUTOS: o.prod,
      ALTERAR_MARCAS: o.marca
    };
    if (o.custo) corpo['CUSTO'] = custo;
    if (o.lucro) corpo['LUCRO'] = lucro;

    this.modalMargemAberto = false;
    this.processandoMargem = true;
    this.margemPct = 0;
    this.margemMsg = 'Atualizando margens no Sankhya...';
    try {
      const res: any = await firstValueFrom(this.sankhyaGateway.chamarServiceGpl('ProdutoGlobalSP.doAtualizarMargensLote', corpo));
      if (res?.status && String(res.status) !== '1') throw new Error(res.statusMessage || 'Erro ao atualizar as margens no Sankhya.');
      const corpoResp = res?.responseBody ?? res;
      this.margemPct = o.prod ? 10 : 100;

      if (o.prod) {
        this.margemMsg = 'Atualizando margens no Aerosys...';
        const job: any = await firstValueFrom(this.produtoService.iniciarMargensAerosys({
          codMarca: this.margemAtualizar === 'FABRICANTE' ? this.margemCodMarca.trim() : '',
          custo,
          lucro
        }));
        if (!job?.sucesso) throw new Error(job?.erro || 'Não foi possível iniciar a atualização no Aerosys.');
        let erroAerosys = '';
        for (let i = 0; i < 3000; i++) { // ate ~40 min
          await new Promise(r => setTimeout(r, 800));
          const p: any = await firstValueFrom(this.produtoService.progressoMargensAerosys(job.jobId));
          this.margemPct = 10 + Math.round((p.percentual || 0) * 0.9);
          if (p.finalizado) { erroAerosys = p.erro || ''; break; }
        }
        if (erroAerosys) throw new Error('Sankhya atualizado, mas o Aerosys falhou: ' + erroAerosys);
      }
      this.margemPct = 100;
      const qtd = corpoResp?.produtosAtualizados;
      this.mostrarAviso(`Margens atualizadas com sucesso${qtd != null && o.prod ? ` (${qtd} produto(s))` : ''}.`);
      this.cachePaginas.clear();
    } catch (err: any) {
      this.mostrarAviso(err?.message ?? 'Erro ao atualizar as margens.', true);
    } finally {
      this.processandoMargem = false;
    }
  }

  exportando = false;
  exportandoMsg = '';
  exportandoPct = 0;
  avisoFuncoes = '';
  avisoErro = false;
  private timerAviso: any = null;

  /** Toast (some sozinho em 4s). */
  private mostrarAviso(msg: string, erro = false): void {
    this.avisoFuncoes = msg;
    this.avisoErro = erro;
    if (this.timerAviso) clearTimeout(this.timerAviso);
    this.timerAviso = setTimeout(() => (this.avisoFuncoes = ''), 4000);
  }

  private readonly colunasExport = ['Código', 'Status', 'CD', 'PN', 'Descrição', 'Fabricante', 'Preço Fab.', 'Unidade', 'U$+IPI', 'R$+IPI',
    'Estoque', 'Local', 'Core U$', 'Serial', 'NCM', 'Label', 'ECCN', 'Schedule B', 'Grupo'];

  /** Busca TODOS os produtos do filtro atual (todas as páginas) + estoque/local/core do Aerosys. */
  private async coletarParaExportar(): Promise<ProdutoBusca[]> {
    const termo = this.termoBusca.trim();
    const todos: ProdutoBusca[] = [];
    const tam = 200;
    let pagina = 1;
    let total = 0;
    do {
      const res = await firstValueFrom(
        this.produtoService.listarProdutosPaginado(termo, pagina, tam, this.empresaId, this.listaCompleta));
      if (!res?.sucesso) throw new Error(res?.erro || 'Erro ao carregar os produtos.');
      total = res.total ?? 0;
      todos.push(...(res.itens || []));
      this.exportandoPct = total > 0 ? Math.min(50, Math.round((todos.length / total) * 50)) : 50;
      pagina++;
      if (!(res.itens || []).length) break;
    } while (todos.length < total);

    this.exportandoPct = 50;
    const empresa = Number(this.empresaStateService.selecionada) || 1;
    for (let i = 0; i < todos.length; i += 200) {
      const parte = todos.slice(i, i + 200);
      const ids = parte.map(p => Number(p.codprod ?? p.codigo)).filter(n => n > 0);
      try {
        const mapa = await firstValueFrom(this.produtoService.buscarEstoqueAerosysLote(ids, empresa).pipe(timeout(60000)));
        for (const p of parte) {
          const e = mapa?.[String(p.codprod ?? p.codigo)];
          p.estoqueAerosys = e ? e.qtde : null;
          p.localAerosys = e ? (e.local || null) : null;
          p.coreAerosys = e ? e.core : null;
        }
      } catch { /* sem Aerosys: colunas em branco */ }
      this.exportandoPct = 50 + Math.round((Math.min(i + 200, todos.length) / todos.length) * 50);
    }
    return todos;
  }

  private linhasExport(itens: ProdutoBusca[]): (string | number | null)[][] {
    return itens.map(p => [
      this.codProdStr(p),
      this.isAtivo(p) ? 'Ativo' : 'Inativo',
      p.ad_condicao || '',
      p.refforn || '',
      p.descricao || '',
      p.marca || '',
      p.precoFab && p.precoFab > 0 ? p.precoFab : null,
      p.codvol || '',
      this.vendaMoeda(p),
      this.vendaReais(p) != null ? Math.round((this.vendaReais(p) as number) * 100) / 100 : null,
      p.estoqueAerosys ?? null,
      p.localAerosys || '',
      p.coreAerosys || null,
      p.tipcontest === 'E' ? 'S' : 'N',
      p.ncm || '',
      p.gpl_label || '',
      p.ad_eccn || '',
      p.ad_scheduleb || '',
      this.getNomeGrupo(p.codgrupoprod) === '—' ? '' : this.getNomeGrupo(p.codgrupoprod)
    ]);
  }

  private nomeArquivo(ext: string): string {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `produtos_${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}.${ext}`;
  }

  private baixar(blob: Blob, nome: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nome;
    a.click();
    URL.revokeObjectURL(url);
  }

  private async executarExport(acao: (linhas: (string | number | null)[][]) => Promise<string> | string): Promise<void> {
    if (this.exportando) return;
    this.funcoesMenuAberto = false;
    this.exportando = true;
    this.exportandoMsg = 'Preparando dados...';
    this.exportandoPct = 0;
    this.avisoFuncoes = '';
    try {
      const itens = await this.coletarParaExportar();
      this.exportandoPct = 100;
      const msg = await acao(this.linhasExport(itens));
      this.mostrarAviso(msg);
    } catch (err: any) {
      this.mostrarAviso(err?.message ?? 'Erro ao exportar os dados.', true);
    } finally {
      this.exportando = false;
      this.exportandoMsg = '';
    }
  }

  exportarExcel(): void {
    void this.executarExport(linhas => {
      const ws = XLSX.utils.aoa_to_sheet([this.colunasExport, ...linhas]);
      ws['!cols'] = this.colunasExport.map((_, i) => ({ wch: i === 4 ? 50 : i === 18 ? 24 : 14 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Produtos');
      XLSX.writeFile(wb, this.nomeArquivo('xlsx'));
      return `${linhas.length} produto(s) exportado(s) para Excel.`;
    });
  }

  exportarCsv(): void {
    void this.executarExport(linhas => {
      const fmt = (v: string | number | null): string => {
        if (v == null) return '';
        const s = typeof v === 'number' ? String(v).replace('.', ',') : String(v);
        return /[;"\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
      };
      const csv = [this.colunasExport, ...linhas].map(l => l.map(fmt).join(';')).join('\r\n');
      this.baixar(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }), this.nomeArquivo('csv'));
      return `${linhas.length} produto(s) exportado(s) para CSV.`;
    });
  }

  copiarParaAreaDeTransferencia(): void {
    void this.executarExport(async linhas => {
      const limpa = (v: string | number | null): string =>
        v == null ? '' : String(v).replace(/[\t\r\n]+/g, ' ');
      const texto = [this.colunasExport, ...linhas].map(l => l.map(limpa).join('\t')).join('\r\n');
      await navigator.clipboard.writeText(texto);
      return `Dados copiados para a área de transferência com sucesso! (${linhas.length} produto(s))`;
    });
  }

  /** Um clique no PN copia o valor (com fallback para contextos sem navigator.clipboard). */
  async copiarPn(p: ProdutoBusca): Promise<void> {
    const pn = String(p.refforn ?? '').trim();
    if (!pn) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(pn);
      } else {
        const ta = document.createElement('textarea');
        ta.value = pn;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.mostrarAviso(`PN copiado: ${pn}`);
    } catch {
      this.mostrarAviso('Não foi possível copiar o PN.', true);
    }
  }

  /** Opcoes de atualizacao de precos/letras (Variavel ou Fixo). Fluxo ainda a definir. */
  acaoFuncaoPreco(funcao: 'TABELA_PRECOS', modo: 'VARIAVEL' | 'FIXO'): void {
    this.funcoesMenuAberto = false;
    const nome = 'Atualizar Tabela de Preços - Arquivo';
    const m = modo === 'VARIAVEL' ? 'Valor Variável' : 'Valor Fixo';
    this.mostrarAviso(`${nome} (${m}): em desenvolvimento.`);
  }

  toggleFuncoesMenu(event?: Event): void {
    event?.stopPropagation();
    this.funcoesMenuAberto = !this.funcoesMenuAberto;
  }
  linhaSelecionada: ProdutoBusca | null = null;

  private inscricao: Subscription | null = null;

  constructor(
    private readonly produtoService: ProdutoService,
    private readonly empresaStateService: EmpresaStateService,
    private readonly sankhyaGateway: SankhyaGatewayService
  ) {}

  ngOnInit(): void {
    void this.carregarCotacaoDolar();
    this.empresaId = this.empresaStateService.selecionada || '';
    this.carregar();
    this.inscricao = this.empresaStateService.empresaMudou.subscribe((codEmp: string) => {
      this.empresaId = codEmp || '';
      this.carregar();
    });
  }

  ngOnDestroy(): void {
    if (this.timerBusca) clearTimeout(this.timerBusca);
    if (this.inscricao) {
      this.inscricao.unsubscribe();
    }
  }

  mapaGrupos = new Map<string, string>();
  mapaLocais = new Map<string, string>();

  /** Total de produtos que atendem à pesquisa (todas as páginas). */
  totalRegistros = 0;
  private timerBusca: any = null;
  private sequenciaBusca = 0;

  /** Recarrega grupos/locais e a página 1 da lista (botão "Atualizar" / troca de empresa). */
  async carregar(): Promise<void> {
    this.carregando = true;
    this.erro = '';
    try {
      const [grupos, locais] = await Promise.all([
        firstValueFrom(this.produtoService.buscarGruposProduto()).catch(() => []),
        firstValueFrom(this.produtoService.buscarLocais()).catch(() => [])
      ]);

      this.mapaGrupos.clear();
      if (grupos) {
        grupos.forEach(g => {
          this.mapaGrupos.set(g.valor, g.label);
        });
      }

      this.mapaLocais.clear();
      if (locais) {
        locais.forEach(l => {
          this.mapaLocais.set(l.valor, l.label);
        });
      }

      await this.carregarPagina(1, false);
    } catch (err: any) {
      this.erro = err?.message ?? 'Erro ao carregar a lista de produtos.';
    } finally {
      this.carregando = false;
    }
  }

  /** Busca UMA página no servidor (a pesquisa também é feita lá, em todas as colunas). */
  private async carregarPagina(pagina: number, mostrarCarregando = true): Promise<void> {
    const seq = ++this.sequenciaBusca;
    if (mostrarCarregando) this.carregando = true;
    this.erro = '';
    try {
      const chave = this.chavePagina(pagina);
      const emCache = this.cachePaginas.get(chave);
      const res = (emCache && Date.now() - emCache.t < 60000)
        ? emCache.res
        : await firstValueFrom(
            this.produtoService.listarProdutosPaginado(this.termoBusca.trim(), pagina, this.itensPorPagina, this.empresaId, this.listaCompleta)
          );
      if (res?.sucesso && !emCache) this.guardarPagina(chave, res);
      if (seq !== this.sequenciaBusca) return; // chegou resposta de uma busca mais antiga
      if (!res?.sucesso) {
        this.erro = res?.erro || 'Erro ao carregar a lista de produtos.';
        this.produtos = [];
        this.totalRegistros = 0;
        return;
      }
      this.produtos = res.itens || [];
      this.totalRegistros = res.total ?? this.produtos.length;
      this.paginaAtual = res.pagina ?? pagina;
      void this.carregarEstoqueAerosys(this.produtos, seq).then(() => {
        if (seq === this.sequenciaBusca) void this.prefetchPagina(this.paginaAtual + 1);
      });
    } catch (err: any) {
      if (seq !== this.sequenciaBusca) return;
      this.erro = err?.message ?? 'Erro ao carregar a lista de produtos.';
    } finally {
      if (seq === this.sequenciaBusca && mostrarCarregando) this.carregando = false;
    }
  }

  /** Preenche Estoque/Local do Aerosys sem bloquear a lista (a tabela aparece antes). */
  carregandoEstoque = false;
  private cacheEstoque = new Map<string, { t: number; qtde: number; local: string | null; core: number }>();

  // ── Pre-carga da proxima pagina (lista + estoque) para a navegacao ficar instantanea ──
  private cachePaginas = new Map<string, { t: number; res: any }>();

  private chavePagina(pagina: number, termo = this.termoBusca.trim()): string {
    return `${termo}|${pagina}|${this.itensPorPagina}|${this.empresaId}|${this.listaCompleta}`;
  }

  private guardarPagina(chave: string, res: any): void {
    if (this.cachePaginas.size > 20) this.cachePaginas.clear();
    this.cachePaginas.set(chave, { t: Date.now(), res });
  }

  private async prefetchPagina(pagina: number): Promise<void> {
    if (pagina < 1 || pagina > this.totalPaginas) return;
    const termo = this.termoBusca.trim();
    const chave = this.chavePagina(pagina, termo);
    if (this.cachePaginas.has(chave)) return;
    try {
      const res = await firstValueFrom(
        this.produtoService.listarProdutosPaginado(termo, pagina, this.itensPorPagina, this.empresaId, this.listaCompleta));
      if (!res?.sucesso) return;
      // So a lista (Sankhya) e pre-carregada; Estoque/Local/Core do Aerosys so sao consultados quando a pagina e aberta,
      // para nao gerar consultas no Firebird de paginas que o usuario talvez nunca abra.
      this.guardarPagina(chave, res);
    } catch { /* pre-carga e opcional */ }
  }

  private async carregarEstoqueAerosys(itens: ProdutoBusca[], seq: number): Promise<void> {
    const ids = itens.map(p => Number(p.codprod ?? p.codigo)).filter(n => n > 0);
    if (!ids.length) return;
    const empresa = Number(this.empresaStateService.selecionada) || 1;
    // Reaproveita o que ja foi buscado (voltar de pagina nao refaz a consulta).
    const faltando: number[] = [];
    for (const p of itens) {
      const c = this.cacheEstoque.get(`${empresa}:${p.codprod ?? p.codigo}`);
      if (c && Date.now() - c.t < 60000) { p.estoqueAerosys = c.qtde; p.localAerosys = c.local; p.coreAerosys = c.core; }
    }
    for (const p of itens) if (p.estoqueAerosys === undefined) faltando.push(Number(p.codprod ?? p.codigo));
    if (!faltando.length) { this.carregandoEstoque = false; return; }
    this.carregandoEstoque = true;
    try {
      const mapa = await firstValueFrom(this.produtoService.buscarEstoqueAerosysLote(faltando, empresa));
      if (seq !== this.sequenciaBusca) return;
      for (const p of itens) {
        const e = mapa?.[String(p.codprod ?? p.codigo)];
        p.estoqueAerosys = e ? e.qtde : null;
        p.localAerosys = e ? (e.local || null) : null;
        p.coreAerosys = e ? e.core : null;
        if (e) this.cacheEstoque.set(`${empresa}:${p.codprod ?? p.codigo}`, { t: Date.now(), qtde: e.qtde, local: e.local || null, core: e.core });
      }
    } catch {
      /* Aerosys indisponivel: colunas ficam com traco */
      for (const p of itens) if (p.estoqueAerosys === undefined) { p.estoqueAerosys = null; p.localAerosys = null; p.coreAerosys = null; }
    } finally {
      if (seq === this.sequenciaBusca) this.carregandoEstoque = false;
    }
  }

  getNomeGrupo(codigo: string | number | undefined): string {
    if (!codigo) return '—';
    const nome = this.mapaGrupos.get(codigo.toString());
    if (nome && nome.includes(' - ')) {
      return nome.substring(nome.indexOf(' - ') + 3).trim();
    }
    return nome ? nome : codigo.toString();
  }

  getNomeLocal(codigo: string | number | undefined): string {
    if (!codigo) return '—';
    const nome = this.mapaLocais.get(codigo.toString());
    if (nome && nome.includes(' - ')) {
      return nome.substring(nome.indexOf(' - ') + 3).trim();
    }
    return nome ? nome : codigo.toString();
  }

  /** A página atual já vem filtrada e paginada do servidor. */
  get produtosFiltrados(): ProdutoBusca[] {
    return this.produtos;
  }

  get produtosPagina(): ProdutoBusca[] {
    return this.produtos;
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.totalRegistros / this.itensPorPagina));
  }

  get paginas(): number[] {
    const total = this.totalPaginas;
    const atual = this.paginaAtual;
    const delta = 2;
    const range: number[] = [];
    for (let i = Math.max(1, atual - delta); i <= Math.min(total, atual + delta); i++) {
      range.push(i);
    }
    return range;
  }

  /** Pesquisa no servidor com pequena espera enquanto o usuário digita. */
  onTermoBuscaChange(): void {
    if (this.timerBusca) clearTimeout(this.timerBusca);
    this.timerBusca = setTimeout(() => void this.carregarPagina(1, false), 350);
  }

  irParaPagina(p: number): void {
    if (p < 1 || p > this.totalPaginas) return;
    void this.carregarPagina(p);
  }

  onItensPorPaginaChange(): void {
    this.itensPorPagina = Number(this.itensPorPagina);
    void this.carregarPagina(1);
  }

  /** Sem informação de ATIVO -- assume ativo, pra não sugerir problema onde não há. */
  isAtivo(p: ProdutoBusca): boolean {
    const v = (p.ativo ?? '').toString().trim().toUpperCase();
    return v === '' || v === 'S';
  }

  codProdStr(p: ProdutoBusca): string {
    return String(p.codprod ?? p.codigo ?? '');
  }

  onNovoProduto(): void {
    // Pesquisou um PN que nao existe: leva o termo para o campo PN do Cadastro.
    const termo = this.termoBusca.trim();
    if (termo && !this.carregando && this.produtos.length === 0) {
      this.novoProduto.emit(termo);
    } else {
      this.novoProduto.emit();
    }
  }

  onEditar(p: ProdutoBusca): void {
    const cod = this.codProdStr(p);
    if (!cod) return;
    this.editarProduto.emit(cod);
  }

  onClonar(p: ProdutoBusca): void {
    const cod = this.codProdStr(p);
    if (!cod) return;
    this.clonarProduto.emit({
      codprod: cod,
      partNumber: p.refforn || '',
      nome: p.descricao || '',
      imagem: ''
    });
  }

  /** Clique em qualquer lugar fora do menu de ações fecha o menu. */
  @HostListener('document:click')
  fecharMenuAcoes(): void {
    this.menuAberto = null;
    this.funcoesMenuAberto = false;
  }

  // ── Valor de venda em U$ e R$ (mesma regra da Consulta de Peças F7) ──
  cotacaoDolar = 0;

  private async carregarCotacaoDolar(): Promise<void> {
    try {
      const res: any = await firstValueFrom(this.sankhyaGateway.buscarCotacoesDemonstrativo());
      const body = res?.responseBody ?? res;
      const valor = parseFloat(String(body?.cotacoes?.[0]?.valor ?? '').replace(',', '.'));
      this.cotacaoDolar = isNaN(valor) ? 0 : valor;
    } catch {
      this.cotacaoDolar = 0;
    }
  }

  /** Preço base em R$ (fabricante nacional não converte; demais = valor × cotação). */
  private baseReais(p: ProdutoBusca): number | null {
    const v = p.vlrVenda != null && p.vlrVenda > 0 ? p.vlrVenda : null;
    if (v == null) return null;
    if ((p as any).origemNacional) return v;
    return this.cotacaoDolar > 0 ? v * this.cotacaoDolar : null;
  }

  /** IPI unitário em R$ (preço R$ × alíquota), igual à Consulta de Peças (F7). */
  private ipiReais(p: ProdutoBusca): number {
    const base = this.baseReais(p);
    const aliq = Number((p as any).aliqIpi) || 0;
    return base != null && aliq > 0 ? parseFloat((base * aliq / 100).toFixed(2)) : 0;
  }

  /** Valor de venda em U$ (ou na moeda do fabricante) já somado o IPI. */
  vendaMoeda(p: ProdutoBusca): number | null {
    if (p.vlrVenda == null || p.vlrVenda <= 0) return null;
    const ipiMoeda = this.cotacaoDolar > 0 ? this.ipiReais(p) / this.cotacaoDolar : 0;
    return parseFloat((p.vlrVenda + ipiMoeda).toFixed(2));
  }

  /** Valor de venda em R$ já somado o IPI. */
  vendaReais(p: ProdutoBusca): number | null {
    const base = this.baseReais(p);
    if (base == null) return null;
    return parseFloat((base + this.ipiReais(p)).toFixed(2));
  }
}
